import BigNumber from "bignumber.js";
import Long from "long";
import { AllocationData, OrderIntent } from "shared/cubiq-model-multi-asset";
import { riskLogger } from "shared/cubiq-services/lookup/model/impl/bmrisk/FwdRiskModel";
import { BmarkOrderData } from "shared/cubiq-services/model/benchmarks/BmarkOrderData.model";
import { BmarkRiskData } from "shared/cubiq-services/model/benchmarks/BmarkRiskData.model";

/**
 * Class describing the forward Risk for a trader Group
 */

export class FwdRiskTGNet {
  /** Map of allocation Ref to base Amt */
  allocationBaseAmtIdx: Map<string, BigNumber> = new Map();
  strDbgPfx: string;

  public netBaseAmt: BigNumber = new BigNumber(0);

  /**
   * Having netFillAmt  makes it easier to process with spot code but
   * we do not track fills for fwd risk
   */
  public netFillAmt: BigNumber = new BigNumber(0);

  /** True if any orders active - if false, all orders are unfilled and terminal  */
  public hasActiveOrders: boolean = false;

  constructor(
    public tgId: Long,
    public tgName: string,
  ) {
    this.strDbgPfx = "[FwdRisk " + this.tgName + (this.tgId ? ":" + this.tgId + "] " : "] ");
  }

  public getNumOrders(): number {
    return this.allocationBaseAmtIdx.size;
  }

  public applyAllocation(
    allocation: AllocationData,
    risk: BmarkRiskData,
    order: BmarkOrderData,
  ): boolean {
    let ret: boolean = false;
    if (!allocation || (!risk && !order)) {
      riskLogger.trace(
        this.strDbgPfx + " : attempt to applyAllocation with neither an order nor a risk",
      );
      return ret;
    }

    let tradingBase: boolean = true;
    let intent: OrderIntent = null;
    let baseAmt: BigNumber = null;

    if (risk) {
      tradingBase = allocation.fixedCcy == allocation.baseCcy;
      intent = this.intent(allocation, tradingBase);
      baseAmt = this.baseAmt(allocation, risk, tradingBase);
    }

    if (null != risk && null != intent && null != baseAmt) {
      this.hasActiveOrders = true;
      ret = this.addAllocation(allocation.allocationRef, intent, baseAmt); // BASE AMT
    }

    return ret;
  }

  public removeAllocation(
    allocation: AllocationData,
    risk: BmarkRiskData,
    order: BmarkOrderData,
  ): boolean {
    let ret: boolean = false;
    if (!allocation || (!risk && !order)) {
      riskLogger.trace(
        this.strDbgPfx + " : attempt to removeAllocation with neither an order nor a risk",
      );
      return ret;
    }

    let tradingBase: boolean = true;
    let intent: OrderIntent = null;
    let baseAmt: BigNumber = null;

    if (risk) {
      tradingBase = allocation.fixedCcy == allocation.baseCcy;
      intent = this.intent(allocation, tradingBase);
      baseAmt = this.baseAmt(allocation, risk, tradingBase);
    }

    if (null != risk && null != intent && null != baseAmt) {
      ret = this.resetRisk(allocation.allocationRef, intent);
    }

    return ret;
  }

  private addAllocation(allocationId: string, intent: OrderIntent, baseAmt: BigNumber): boolean {
    let ret: boolean = false;
    let knownOrderBase: BigNumber = this.allocationBaseAmtIdx.get(allocationId);
    if (!knownOrderBase || !knownOrderBase.isEqualTo(baseAmt)) {
      this.netBaseAmt =
        intent == OrderIntent.BUY ? this.netBaseAmt.plus(baseAmt) : this.netBaseAmt.minus(baseAmt);

      this.allocationBaseAmtIdx.set(allocationId, baseAmt);
      // prettier-ignore
      riskLogger.debug(this.strDbgPfx + " : addRisk id[" + allocationId + "] +Base[" + (intent == OrderIntent.BUY ? "B" : "S") + ":" + baseAmt + "] => netBase[" + this.netBaseAmt + "] ");
      ret = true;
    } else {
      if (!knownOrderBase) {
        // prettier-ignore
        riskLogger.debug(this.strDbgPfx + " : addRisk ? id[" + allocationId + "] not found in riskBaseAmtIdx - NOP");
      } else {
        // prettier-ignore
        riskLogger.debug(this.strDbgPfx + " : addRisk ? id[" + allocationId + "] baseAmt Same:[" + knownOrderBase + "] - NOP");
      }
    }
    return ret;
  }

  private resetRisk(allocationId: string, intent: OrderIntent): boolean {
    let ret: boolean = false;
    let currentRiskAmt: BigNumber = this.allocationBaseAmtIdx.get(allocationId);
    if (null != currentRiskAmt) {
      // For SWaps this'll be a NOP
      this.netBaseAmt =
        intent == OrderIntent.BUY
          ? this.netBaseAmt.minus(currentRiskAmt)
          : this.netBaseAmt.plus(currentRiskAmt);
      this.allocationBaseAmtIdx.delete(allocationId);
      if (this.allocationBaseAmtIdx.size == 0) {
        this.hasActiveOrders = false;
      }

      // prettier-ignore
      riskLogger.debug(this.strDbgPfx + " : resetRisk id [" + allocationId + "] -Base[" + (intent == OrderIntent.BUY ? "B" : "S") + ":" + currentRiskAmt + "] => netBase[" + this.netBaseAmt + "] ");
      ret = true;
    } else {
      // prettier-ignore
      riskLogger.debug(this.strDbgPfx + " : resetRisk ? id[" + allocationId + "] not found in riskBaseAmtIdx - NOP");
    }
    return ret;
  }

  private intent(allocation: AllocationData, tradingBase: boolean): OrderIntent {
    let intent: OrderIntent = tradingBase
      ? allocation.allocationIntent
      : allocation.allocationIntent == OrderIntent.BUY
        ? OrderIntent.SELL
        : OrderIntent.BUY;
    return intent;
  }

  private baseAmt(
    allocation: AllocationData,
    risk: BmarkRiskData,
    tradingBase: boolean,
  ): BigNumber {
    // We use order amount, not NPV amount
    let baseAmt: BigNumber = tradingBase ? allocation.allocationQty : allocation.settleQty;
    return baseAmt;
  }

  public hasAllocation(allocationId: string): boolean {
    return this.allocationBaseAmtIdx != null && this.allocationBaseAmtIdx.has(allocationId);
  }

  public toString(): string {
    return `${this.strDbgPfx} [Num: ${this.getNumOrders()}] [Base: ${this.netBaseAmt}]`;
  }
}
